/**
 * @file ContractDrawerExtractionBodyContainer.tsx
 * @description Thin container for the drawer's Contract tab body when a
 *   loaded CONTRACT extraction is present. Historically this container
 *   also loaded the sibling `TRANSFER_AGREEMENT` extraction and forwarded
 *   it to the shared `ContractExtractionView` atom with `showTabs`. That
 *   coupling was the root cause of SE-4605: the three-tab strip only
 *   mounted when a CONTRACT extraction existed, so players with zero
 *   uploaded documents saw a single generic empty state instead of the
 *   Contract / Transfer Agreement / Live Tracking tabs.
 *
 *   SE-4605 hoists the tab strip up to `ContractDrawerView.renderDrawerBody`
 *   so tabs render regardless of whether the primary extraction exists.
 *   The sibling TRANSFER_AGREEMENT fetch is owned at
 *   `ContractDrawerContainer` (one level up), which threads the resolved
 *   extraction into the drawer view — this container therefore no
 *   longer owns the fetch and no longer passes `showTabs` to the
 *   shared atom. The body renders as the Contract tab's content only.
 * @module features/contracts/components/ContractDrawerExtractionBody/ContractDrawerExtractionBodyContainer
 */

import React, { memo } from "react";

import type { ContractExtraction } from "@/features/contracts/types";

import ContractDrawerExtractionBodyView from "./ContractDrawerExtractionBodyView";

/** Props for {@link ContractDrawerExtractionBodyContainer}. */
interface Props {
  /** Loaded primary extraction the body renders against. */
  readonly extraction: ContractExtraction;
  /** Player UUID from the drawer state, possibly null while transitioning. */
  readonly playerId: string | null;
  /** Document type (drawer state). */
  readonly documentType: string;
  /** Whether the drawer is in read-only mode. */
  readonly isReadOnly: boolean;
  /** Whether SAVED view mode is active (no editing). */
  readonly isSavedViewMode: boolean;
  /** Whether the SAVED extraction is being edited. */
  readonly isEditMode: boolean;
  /** Whether to offer the Save button. */
  readonly canSave: boolean;
  /** Handler for the Discard button. */
  readonly onOpenDeleteDialog: () => void;
  /** Handler for the Edit button. */
  readonly onEnterEditMode: () => void;
  /** Handler for the Cancel button in edit-mode banner. */
  readonly onRequestCancel: () => void;
  /** Handler for the "+ Add Document" CTA. */
  readonly onAddDocumentPress: () => void;
}

/**
 * Resolves the drawer body's affordance gates.
 *
 * This was a pass-through whose own note said it existed so "future
 * drawer-local concerns have a stable home". These six gates are that: the View
 * was computing every one of them from other props, which is a decision it
 * should have been handed (SE-8019).
 * @param props - Component props
 * @returns The wired drawer body view
 */
function ContractDrawerExtractionBodyContainer(props: Props) {
  const {
    extraction,
    playerId,
    isReadOnly,
    isSavedViewMode,
    isEditMode,
    canSave,
  } = props;

  // Fall back to extraction.playerId when the drawer state's playerId is null
  // during a transition so downstream save/refetch calls always receive a
  // valid non-empty ID.
  const resolvedPlayerId = playerId ?? extraction.playerId;

  // The data atom is read-only when the caller has flagged the whole drawer
  // read-only (non-OWNER, archived, or SAVED) OR when we are in SAVED
  // view-mode specifically. When the user has explicitly entered edit mode
  // (SAVED + OWNER + not archived) the drawer is still flagged read-only by
  // the container formula but isEditMode forces the inline atom out of
  // read-only regardless.
  const dataAtomReadOnly = (isReadOnly || isSavedViewMode) && !isEditMode;

  // SE-4767: Discard + Add Document render on the same horizontal row as the
  // StatusStrip's Edit button rather than stacking vertically. This gates the
  // row-inlined Discard; the inline Discard inside `ContractExtractionView` is
  // pinned false so it does not double-render.
  const showDiscardButton = !isReadOnly && !isSavedViewMode && !isEditMode;

  // SE-4767 originally pinned Save to `!isEditMode`, so it disappeared the
  // moment the user clicked Edit. SE-4768 splits the affordance across two
  // surfaces: inside edit mode it lives in the EditModeBanner's `saveAction`
  // slot (paired visually with Cancel), and outside it stays in the
  // StatusStrip's `extraActions` slot. The PENDING_REVIEW gate (`canSave`)
  // holds for both, so Save appears only when there is something to commit.
  const showSaveButtonInStrip = canSave && !isReadOnly && !isEditMode;
  const showSaveButtonInBanner = canSave && !isReadOnly && isEditMode;

  const showAddDocumentAction = isSavedViewMode && !isReadOnly;

  return (
    <ContractDrawerExtractionBodyView
      {...props}
      resolvedPlayerId={resolvedPlayerId}
      dataAtomReadOnly={dataAtomReadOnly}
      showDiscardButton={showDiscardButton}
      showSaveButtonInStrip={showSaveButtonInStrip}
      showSaveButtonInBanner={showSaveButtonInBanner}
      showAddDocumentAction={showAddDocumentAction}
    />
  );
}

ContractDrawerExtractionBodyContainer.displayName =
  "ContractDrawerExtractionBodyContainer";

export default memo(ContractDrawerExtractionBodyContainer);
